<template>
  <div
    :class="[
      $style.gamesCatalogueWrapper,
      { [$style.gamesCatalogueWrapperNoMargin]: isDefaultCollection },
    ]"
  >
    <CatalogueHeader
      v-if="catalogueTitle && !isFavoritsCollection"
      no-navigation
      no-show-all-button
      :class="$style.gamesCatalogueWrapperTitle"
      :icon="catalogueIcon ? catalogueIcon.url : undefined"
      :name="catalogueTitle"
    />
    <div v-if="isDefaultCollection" :class="$style.gamesCatalogueContentDefault">
      <MainPageCatalogueCarousel
        :collections="storeCategoryPage.gameRowsCategories"
        :show-all-title="storeCataloguePage.showAllGamesTitle"
        :data-show-more-title="storeButtonTitles.showMoreGames"
        :gtm-action="gtmAction"
      />
    </div>
    <MyGamesCategory
      v-else-if="isFavoritsCollection"
      :class-for-container="$style.gamesCatalogueContent"
      :gtm-action="gtmAction"
      :suggestion-collection="collectionHits"
      @addToFavorite="addToFavorite"
      @removeFromFavorite="removeFromFavorite"
      @setCountCatalogueItem="setCountCatalogueItem"
    />
    <MainPageCatalogueContainer
      v-else
      :class="$style.gamesCatalogueContent"
      :games-list="gamesDataListFormatted"
      :gtm-action="gtmAction"
      :gtm-data="{ event: 'game_open', event_label: '' }"
      :favorite-games="favoriteGames"
      :is-loading="!isGamesLoaded"
      @addToFavorite="addToFavorite"
      @removeFromFavorite="removeFromFavorite"
      @setCountCatalogueItem="setCountCatalogueItem"
    />
    <div
      v-if="
        gamesDataList &&
        isMoreGames &&
        isGamesLoaded &&
        !isDefaultCollection &&
        !isFavoritsCollection
      "
      :class="$style.gamesCatalogueShowMore"
    >
      <BitButton type="secondary" @click="showMoreGames()">
        {{ storeButtonTitles.showMoreGames }}
      </BitButton>
    </div>
    <div v-else-if="isNoGames && !isDefaultCollection">
      <p :class="$style.gamesCatalogueNoGames">
        {{ storeCataloguePage.noGamesTitle }}
      </p>
      <CatalogueRow
        :collection="collectionHits"
        :games-list="hitsGames"
        :show-all-title="storeCataloguePage.showAllGamesTitle"
        :gtm-action="gtmAction"
        :header-text="storeCataloguePage.noGamesButWeRecommendForYou"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { BitButton } from '@bit/ui-kit';
import MainPageCatalogueCarousel from '~/components/mainPage/catalogueCarousel.vue';
import MainPageCatalogueContainer from '~/components/mainPage/catalogueContainer.vue';
import CatalogueRow from '~/components/mainPage/catalogueRow.vue';
import CatalogueHeader from '~/components/mainPage/catalogueSliderHeader.vue';
import MyGamesCategory from '~/components/mainPage/myGamesCategory.vue';
import { useLoadSliderGames } from '~/composables/useLoadSliderGames';
import { useStoreModals } from '~/stores/abstract/modals';
import { useStoreRoot } from '~/stores/abstract/root';
import { useStoreSsGames } from '~/stores/softswiss/games';
import { useStoreSsGamesFavourite } from '~/stores/softswiss/gamesFavourite';
import { useStoreSsPlayer } from '~/stores/softswiss/player';
import { useStoreButtonTitles } from '~/stores/strapi/buttonTitles';
import { useStoreCataloguePage } from '~/stores/strapi/cataloguePage';
import { useStoreCategoryPage } from '~/stores/strapi/categoryPage';
import { useStoreCurrencyMenu } from '~/stores/strapi/currencyMenu';
import { useStoreProviders } from '~/stores/strapi/providers';
import { useStoreSlotTheme } from '~/stores/strapi/slotThemes';
import { EPageType } from '~/types/pageType';
import type { IGamesRequestOptions, ISsGame } from '~/types/softswiss/ssGames';
import type { IStrapiImage } from '~/types/strapiImage';

const props = defineProps({
  pageType: {
    type: String,
    required: false,
    default: () => '',
  },
});

const route = useRoute();

const storeCategoryPage = useStoreCategoryPage();
const storeSlotTheme = useStoreSlotTheme();
const storeSsGames = useStoreSsGames();
const storeCataloguePage = useStoreCataloguePage();
const storeSsPlayer = useStoreSsPlayer();
const storeRoot = useStoreRoot();
const storeSsGamesFavourite = useStoreSsGamesFavourite();
const storeProviders = useStoreProviders();
const storeButtonTitles = useStoreButtonTitles();
const storeModals = useStoreModals();
const storeCurrencyMenu = useStoreCurrencyMenu();
const { loadCollectionGames } = useLoadSliderGames();

const offsetCount = ref(0);
const gamesCount = ref<number>(NaN);
const pageCount = ref<number>(NaN);
const isGamesLoaded = ref<boolean>(false);
const gamesDataList = ref<ISsGame[]>([]);
const gamesDataListMeta = ref<any>(null);
const hitsGames = ref<ISsGame[] | null>(null);
const catalogueTitle = ref('');
const catalogueIcon = ref<null | IStrapiImage>(null);

const isDefaultCollection = computed(
  () =>
    storeCataloguePage.selectedCollection === storeCataloguePage.defaultCollection &&
    storeCataloguePage.selectedProvider === ''
);

const isFavoritsCollection = computed(() => storeCataloguePage.selectedCollection === 'favorites');

const collectionHits = computed(() => {
  const hits = storeCategoryPage.gameCategories?.find((item) => item.categoryId === 'hits');
  return {
    id: hits?.categoryId,
    name: hits?.categoryTitle,
    icon: hits?.categoryMenuIcon,
  };
});

const isNoGames = computed(() => !gamesDataList.value.length && isGamesLoaded.value);

const isMoreGames = computed((): boolean => {
  if (isFavoritsCollection.value && !storeSsGamesFavourite.gameIds.length) {
    return false;
  }

  if (gamesDataList.value && gamesDataList.value.length && gamesDataListMeta.value) {
    return gamesCount.value < gamesDataListMeta.value.total;
  }

  return false;
});

const gtmAction = computed((): string => {
  if (storeCataloguePage.selectedProvider) {
    return 'provider_' + storeCataloguePage.selectedProvider;
  }

  return (
    'collection_' + (storeCataloguePage.selectedCollection || storeCataloguePage.defaultCollection)
  );
});

const favoriteGames = computed(() => storeSsGamesFavourite.gameIds);
const addToFavorite = storeSsGamesFavourite.addToFavorite;
const removeFromFavorite = storeSsGamesFavourite.removeFromFavorite;

const showMoreGames = (): void => {
  gamesCount.value += pageCount.value;
  loadPaginateGamesDataList();
};

const loadGames = async (additionalParams?: IGamesRequestOptions) => {
  let params: IGamesRequestOptions = {
    limit: pageCount.value,
  };

  if (
    storeSsPlayer.isPlayerAuthorised &&
    storeCurrencyMenu.isFiatCurrency(storeRoot.userCurrency)
  ) {
    params.currency = storeSsPlayer.playerInfo.currency;
  }

  if (storeCataloguePage.selectedProvider) {
    params.provider = storeCataloguePage.selectedProvider;
  }

  if (
    storeCataloguePage.selectedCollection &&
    storeCataloguePage.selectedCollection !== storeCataloguePage.defaultCollection &&
    !isFavoritsCollection.value
  ) {
    params.collection = storeCataloguePage.selectedCollection;
  }

  params = { ...params, ...additionalParams };

  return await storeSsGames.loadGames(params);
};

const loadGamesWithFavoritesReload = async () => {
  isGamesLoaded.value = false;

  const { data, meta } = await loadGames();
  isGamesLoaded.value = true;

  gamesDataList.value = data;
  gamesDataListMeta.value = meta;
  if (gamesDataList.value?.length) {
    offsetCount.value = pageCount.value;
  } else if (collectionHits.value?.id) {
    hitsGames.value = await loadCollectionGames(collectionHits.value?.id);
  }
};

const loadPaginateGamesDataList = async () => {
  const params: IGamesRequestOptions = {
    offset: offsetCount.value,
  };

  const { data } = await loadGames(params);

  gamesDataList.value?.push(...data);
  storeCategoryPage.setCategoryDataList(route.path, {
    gamesCount: gamesCount.value,
    games: gamesDataList.value,
    gamesMeta: gamesDataListMeta.value,
    gamesPerPage: pageCount.value,
  });
  offsetCount.value += pageCount.value;
};

const setCountCatalogueItem = async (count: number) => {
  const savedDataList = storeCategoryPage.categoryDataList[route.path];
  if (savedDataList) {
    return;
  }
  gamesCount.value = count;
  pageCount.value = count;
  if (!isDefaultCollection.value) {
    await loadGamesWithFavoritesReload();
  }
};

watch(
  () => [storeRoot.userCurrency, storeSsPlayer.isPlayerAuthorised],
  async () => {
    if (!isNaN(gamesCount.value)) {
      await loadGamesWithFavoritesReload();
    }
  }
);

watch(
  () => storeSsGamesFavourite.gameIds.length,
  async () => {
    if (storeModals.activeModal?.name === 'search') {
      const { data, meta } = await loadGames();
      gamesDataList.value = data;
      gamesDataListMeta.value = meta;
    }
  },
  { immediate: true }
);

const setCatalogueTitle = () => {
  if (props.pageType === EPageType.slotTheme) {
    catalogueTitle.value =
      storeSlotTheme.list.find((c) => c.categoryId === storeCataloguePage.selectedCollection)
        ?.title || '';
  } else if (storeCataloguePage.selectedProvider) {
    catalogueTitle.value =
      storeProviders.activeList.find((p) => p.providerId === storeCataloguePage.selectedProvider)
        ?.providerTitle || '';
  } else if (storeCataloguePage.selectedCollection && !isDefaultCollection.value) {
    catalogueTitle.value =
      storeCategoryPage.activeList.find(
        (c) => c.categoryId === storeCataloguePage.selectedCollection
      )?.categoryTitle || '';
  } else {
    catalogueTitle.value = '';
  }
};

const setCatalogueIcon = () => {
  if (storeCataloguePage.selectedProvider) {
    catalogueIcon.value =
      storeProviders.activeList.find((p) => p.providerId === storeCataloguePage.selectedProvider)
        ?.providerIcon || null;
  } else if (storeCataloguePage.selectedCollection && !isDefaultCollection.value) {
    catalogueIcon.value =
      storeCategoryPage.list.find((el) => el.categoryId === storeCataloguePage.selectedCollection)
        ?.categoryMenuIcon || null;
  } else {
    catalogueIcon.value = null;
  }
};

const gamesDataListFormatted = computed(() => {
  if (isFavoritsCollection.value && gamesDataList.value?.length) {
    return storeSsGamesFavourite.gameIds
      .map((gameId) => {
        return {
          gameId,
          ...gamesDataList.value.find((g) => Object.values(g.real).some((r) => r.id === gameId)),
        };
      })
      .filter((game) => !!game.collections);
  } else {
    return gamesDataList.value.length ? gamesDataList.value : null;
  }
});

// @TODO: DO NOT FUCKING TOUCH IT, IT FIX THE BEST BROWSER IN THE WORLD MOZILLA FIREFOX

watch(
  () => [
    storeCataloguePage.selectedProvider,
    props.pageType,
    storeCataloguePage.selectedCollection,
    isDefaultCollection,
  ],
  () => {
    setCatalogueTitle();
    setCatalogueIcon();
  },
  { immediate: true, deep: true }
);

const onScrollHandler = () => {
  storeCategoryPage.setCategoryScrollPosition(window.scrollY);
};

onBeforeMount(() => {
  if (isDefaultCollection.value) {
    isGamesLoaded.value = true;
  }
  const savedDataList = storeCategoryPage.categoryDataList[route.path];
  if (savedDataList) {
    gamesCount.value = savedDataList.gamesCount;
    offsetCount.value = savedDataList.gamesCount;
    gamesDataList.value = savedDataList.games;
    gamesDataListMeta.value = savedDataList.gamesMeta;
    pageCount.value = savedDataList.gamesPerPage;
    isGamesLoaded.value = true;
  }
});

onMounted(() => {
  window.addEventListener('scroll', onScrollHandler);
});
onBeforeUnmount(() => {
  window.removeEventListener('scroll', onScrollHandler);
});
</script>

<style lang="scss" module src="./catalogue.scss"></style>
